import {sendRefTelemetry} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {CopilotChatRepo, CopilotSpace} from '@github-ui/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {useRef, type RefObject} from 'react'

import type {CopilotSpaceResource, GitHubFileFormData, ResourceCounts} from '../types'
import {useGetCopilotSpaceResourceSizes} from '../hooks/use-get-copilot-space-resource-sizes'
import {MultiFilePicker} from './MultiFilePicker'
import {isRepoInSpaceScope} from '../utils/space-source-scope'

interface SpacesFilePickerProps {
  isOpen: boolean
  onClose: () => void
  onResourcesAdded: (resources: CopilotSpaceResource[]) => Promise<void> | void
  attachmentButtonRef?: RefObject<HTMLButtonElement | null>
  owner?: string
  resourceCounts?: ResourceCounts
  initialRepo?: CopilotChatRepo | null
  onRepoSelect?: (repo: CopilotChatRepo | null) => void
  hideBackButton?: boolean
}

export function getSpacesFilePickerOwner(
  space: Pick<CopilotSpace, 'allowsPrivateReposFromOtherOrgs' | 'owner' | 'ownerIsOrg'>,
): string | undefined {
  if (!space.ownerIsOrg && copilotFeatureFlags.spacesOrgScoping) return space.owner
  return space.ownerIsOrg && !space.allowsPrivateReposFromOtherOrgs ? space.owner : undefined
}

export function SpacesFilePicker({
  isOpen,
  onClose,
  onResourcesAdded,
  attachmentButtonRef,
  owner,
  resourceCounts,
  initialRepo,
  onRepoSelect,
  hideBackButton,
}: SpacesFilePickerProps) {
  const fallbackAttachmentButtonRef = useRef<HTMLButtonElement>(null)
  const {getCopilotSpaceResourceSizes} = useGetCopilotSpaceResourceSizes()

  const saveGitHubResources = async (data: GitHubFileFormData[]) => {
    const resources = data.filter(
      (item): item is GitHubFileFormData & {type: 'github_file'} => item.type === 'github_file',
    )

    if (resources.length === 0) {
      onClose()
      return
    }

    const validationResult = await getCopilotSpaceResourceSizes({
      resources: resources as CopilotSpaceResource[],
    })
    const hydratedResources = resources.map(resource => ({
      ...resource,
      fileExists: true,
      markedForDestroy: false,
      sizePercentage: validationResult[resource.id],
    }))

    await onResourcesAdded(hydratedResources)
    sendRefTelemetry(hydratedResources, 'file-picker')
    onClose()
  }

  if (!isOpen) return null

  return (
    <MultiFilePicker
      formData={[]}
      onCancel={onClose}
      onSaveFiles={saveGitHubResources}
      attachmentButtonRef={attachmentButtonRef ?? fallbackAttachmentButtonRef}
      owner={owner}
      resourceCounts={resourceCounts}
      initialRepo={
        initialRepo &&
        copilotFeatureFlags.spacesOrgScoping &&
        !isRepoInSpaceScope({isPrivate: initialRepo.visibility !== 'public', ownerLogin: initialRepo.ownerLogin}, owner)
          ? null
          : initialRepo
      }
      onRepoSelect={onRepoSelect}
      hideBackButton={hideBackButton}
      maxFileCount={50}
    />
  )
}
